import { zhEn, posTierPlan } from "./posTier.js";
// Ticket + refund maths. MUST match checkout_sale / refund_sale in
// supabase/migrations/pos_phase1.sql — the server re-prices everything and
// rejects a sale whose tenders don't add up to its own total.
import { round2 } from "./posUi.jsx";

export const newId = () => (typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID()
  : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => { const r = Math.random() * 16 | 0; return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16); }));

// The price a product sells at today (sale price inside its window).
// Local (shop) date — toISOString() is UTC and flipped sale prices at 08:00 HKT.
export const localToday = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };
export const effectivePrice = (p, today = localToday()) => {
  if (p?.salePrice != null && p.salePrice !== "" &&
      (!p.saleFrom || p.saleFrom <= today) && (!p.saleTo || p.saleTo >= today)) return Number(p.salePrice) || 0;
  return Number(p?.price) || 0;
};

// A product/custom line: qty × unitPrice − discount (never below 0).
export const lineTotal = (l) => {
  if (l.kind === "appointment") return round2(l.due || 0);
  if (l.kind === "wallet_topup") return round2(l.amount || 0);
  return Math.max(0, round2((Number(l.qty) || 0) * (Number(l.unitPrice) || 0) - (Number(l.discount) || 0)));
};

// discount% → absolute line discount, rounded to cents.
export const pctDiscount = (l, pct) => round2((Number(l.qty) || 0) * (Number(l.unitPrice) || 0) * (Number(pct) || 0) / 100);

export const ticketTotals = (lines, orderDiscount = 0) => {
  let apt = 0, retail = 0, topup = 0, items = 0;
  for (const l of lines) {
    const t = lineTotal(l);
    if (l.kind === "appointment") apt += t;
    else if (l.kind === "wallet_topup") topup += t;
    else { retail += t; items += Number(l.qty) || 0; }
  }
  const od = Math.min(Math.max(0, Number(orderDiscount) || 0), retail);
  return { apt: round2(apt), retail: round2(retail), topup: round2(topup), orderDiscount: round2(od), total: round2(apt + retail - od + topup), items };
};

// Server payload for checkout_sale.
export const buildSalePayload = ({ saleId, tenantId, customerId, staffId, note, orderDiscount, heldTicketId, lines, payments }) => ({
  id: saleId,
  tenantId,
  customerId: customerId || null,
  staffId: staffId || null,
  note: note || "",
  orderDiscount: Number(orderDiscount) || 0,
  heldTicketId: heldTicketId || null,
  lines: lines.map(l => {
    if (l.kind === "appointment") return { kind: "appointment", appointmentId: l.aptId, name: l.name };
    if (l.kind === "wallet_topup") return { kind: "wallet_topup", amount: Number(l.amount) || 0, bonus: Number(l.bonus) || 0 };
    const base = { kind: l.kind, qty: Number(l.qty) || 1, unitPrice: Number(l.unitPrice) || 0, discount: Number(l.discount) || 0, name: l.name };
    if (l.kind === "product") Object.assign(base, {
      productId: l.productId, originalPrice: l.listPrice,
      discountPct: l.discountPct || undefined, override: l.override || undefined, note: l.note || undefined,
      promoId: l.promoId || undefined, promoName: l.promoName || undefined,
    });
    return base;
  }),
  payments: payments.map(p => ({
    method: p.method, methodName: p.methodName || "", amount: round2(p.amount),
    ...(p.tendered != null ? { tendered: round2(p.tendered) } : null),
    ...(p.points ? { points: p.points } : null),
  })),
});

// Refund value of `qty` units of an original sale line — refund_sale's formula.
export const refundLineValue = (sale, line, qty) => {
  const lines = sale.sale_lines || [];
  const lq = Number(line.qty) || 0;
  if (!lq || !qty) return 0;
  if (line.kind === "appointment") return round2(Number(line.total) * qty / lq);
  const retailNet = lines.filter(x => x.kind === "product" || x.kind === "custom").reduce((s, x) => s + Number(x.total), 0);
  let v = Number(line.total);
  const od = Number(sale.order_discount) || 0;
  if (retailNet > 0 && od > 0) v = v - od * Number(line.total) / retailNet;
  return round2(Math.max(0, v) * qty / lq);
};

// Friendly text for the RPC exception codes.
export const posErrorText = (e) => {
  const m = String(e?.message || e || "");
  const map = [
    ["insufficient_wallet", zhEn("錢包餘額不足", "Not enough wallet balance")],
    ["insufficient_points", zhEn("積分不足", "Not enough points")],
    ["wallet_amount_must_be_whole_dollars", zhEn("錢包只可以用整數金額", "The wallet can only take whole dollars")],
    ["held_ticket_gone", zhEn("呢張掛單已經喺另一部機結咗帳或者刪除咗。確定未收錢嘅話，撳「清空」再重新加貨。", "This held ticket was already paid or deleted on another device. If it hasn't been paid, tap \"Clear\" and add the items again.")],
    ["payment_mismatch", zhEn("付款總額同應付金額唔夾 — 預約可能已經喺另一部機收咗款，請重新揀", "The payments don't add up to the amount due — the booking may already have been paid on another device. Please choose again")],
    ["refund_mismatch", zhEn("退款金額同退貨價值唔夾，請再檢查", "The refund amount doesn't match the value of the returned items — please check again")],
    ["stored_value_cannot_buy_topup", zhEn("唔可以用錢包或積分去儲值", "Wallet or points can't be used to top up")],
    ["stored_value_needs_customer", zhEn("用錢包 / 積分要先揀客戶", "Choose a customer first to use wallet / points")],
    ["topup_needs_customer", zhEn("儲值要先揀客戶", "Choose a customer first to top up")],
    ["membership_not_found", zhEn("呢位客戶未有會員記錄", "This customer has no membership record")],
    ["product_not_sellable", zhEn("有商品已下架，唔可以賣", "An item has been taken off sale and can't be sold")],
    ["product_not_found", zhEn("搵唔到商品（可能已刪除）", "Product not found (it may have been deleted)")],
    ["appointment_not_found", zhEn("搵唔到預約", "Booking not found")],
    ["appointment_customer_mismatch", zhEn("預約唔屬於呢位客戶", "The booking doesn't belong to this customer")],
    ["appointment_payment_skipped", zhEn("預約付款被重複保護擋咗，請重新整理再試", "The booking payment was blocked by duplicate protection — refresh and try again")],
    ["qty_exceeds_sold", zhEn("退貨數量多過賣出數量", "Return quantity is more than the quantity sold")],
    ["topup_not_refundable", zhEn("儲值唔可以喺度退款", "Top-ups can't be refunded here")],
    ["exchange_credit_mismatch", zhEn("換貨抵扣金額唔夾", "The exchange credit doesn't match")],
    ["sale_not_refundable", zhEn("呢張單唔可以退款", "This sale can't be refunded")],
    ["pos_not_enabled", zhEn("收銀系統未開通", "The POS isn't switched on")],
    ["forbidden", zhEn("你冇權限做呢個操作", "You don't have permission to do this")],
    ["points_value_mismatch", zhEn("積分換算唔夾", "The points value doesn't match")],
    ["no_lines", zhEn("購物車係空嘅", "The cart is empty")],
    ["staff_cannot_discount", zhEn("員工冇權限打折，請店主操作", "Staff can't give discounts — ask the owner")],
    ["staff_cannot_override_price", zhEn("員工冇權限改價，請店主操作", "Staff can't change prices — ask the owner")],
    ["staff_cannot_refund", zhEn("員工冇權限退款，請店主操作", "Staff can't refund — ask the owner")],
    ["staff_no_revenue", zhEn("員工冇權限睇營收數據，請店主喺員工權限開「可查看營收數據」", "Staff can't see revenue — the owner can switch on \"can view revenue\" in the staff permissions")],
    ["staff_cannot_close_shift", zhEn("員工冇權限交更，請店主操作", "Staff can't close the shift — ask the owner")],
    ["shift_closed", zhEn("呢更已經交咗", "This shift is already closed")],
    ["product_has_variants", zhEn("呢件貨有款式，請揀款式先加入", "This product has variants — choose a variant first")],
    ["promo_exhausted", zhEn("優惠已經用完（次數上限），請重新計過", "The promotion has been used up (usage limit) — please recalculate")],
  ];
  const lim = /pos_product_limit:(\d+)/.exec(m);
  if (lim) return zhEn("你嘅方案最多 " + lim[1] + " 件商品 — 升級方案可以加更多", "Your plan allows up to " + lim[1] + " products — upgrade to add more");
  const need = /pos_plan_required:(\w+)/.exec(m);
  if (need) return zhEn("呢個功能要" + (posTierPlan(need[1]) || "更高方案") + "先用得到", "This feature needs the " + (posTierPlan(need[1]) || "a higher") + " plan");
  for (const [k, v] of map) if (m.includes(k)) return v;
  if (/net-timeout|Failed to fetch|NetworkError|timeout/i.test(m)) return zhEn("網絡唔穩定 — 請再按一次（唔會重複收錢）", "The connection is unstable — tap again (you won't be charged twice)");
  return m.slice(0, 160) || zhEn("出錯了", "Something went wrong");
};

// A held ticket (掛單) older than a day no longer reserves stock — a
// forgotten one would otherwise keep items off sale for good. It can still
// be recalled; it is just shown as 過期.
export const HELD_TTL_MS = 24 * 3600 * 1000;
export const heldStale = (h, now = Date.now()) => now - new Date(h?.updated_at || h?.created_at || 0).getTime() > HELD_TTL_MS;
